<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.company.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right" v-if="isEditModernTrade">
          <base-button size="md" type="neutral" @click="openCreate">
            <i class="fa fa-plus"></i> {{ $t("modern_trade.company.create") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row">
            <div class="col-md-5">
              <base-input
                v-model="search"
                prepend-icon="fas fa-search"
                :placeholder="$t('search')"
                @keyup.enter.native="loadData"
              ></base-input>
            </div>
            <div class="col-md-3">
              <base-input>
                <el-select
                  v-model="statusFilter"
                  :placeholder="$t('modern_trade.company.status')"
                  clearable
                  @change="loadData"
                  class="w-100"
                >
                  <el-option :label="$t('modern_trade.company.active')" :value="1"></el-option>
                  <el-option :label="$t('modern_trade.company.inactive')" :value="0"></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-4 text-right">
              <base-button size="sm" type="primary" @click="loadData">
                <i class="fa fa-search"></i> {{ $t("search") }}
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table
            :data="rows"
            row-key="id"
            header-row-class-name="thead-light"
          >
            <el-table-column :label="$t('modern_trade.company.name')" min-width="200">
              <template slot-scope="{ row }">{{ row.name }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.company.tax_id')" min-width="160">
              <template slot-scope="{ row }">
                {{ row.tax_id }}<span v-if="row.branch_code" class="text-muted ml-1">({{ row.branch_code }})</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.company.contact_name')" min-width="150">
              <template slot-scope="{ row }">
                {{ row.contact_name || "-" }}
                <small v-if="row.contact_tel" class="d-block text-muted">{{ row.contact_tel }}</small>
              </template>
            </el-table-column>
            <el-table-column :label="profileLabels.gp_percent" min-width="90" align="right">
              <template slot-scope="{ row }">
                {{ row.gp_percent !== undefined && row.gp_percent !== null ? Number(row.gp_percent).toFixed(2) : "-" }}
              </template>
            </el-table-column>
            <el-table-column :label="profileLabels.credit_term_days" min-width="90" align="right">
              <template slot-scope="{ row }">
                {{ row.credit_term_days !== undefined && row.credit_term_days !== null ? row.credit_term_days : "-" }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.company.status')" min-width="100" align="center">
              <template slot-scope="{ row }">
                <span class="badge" :class="row.status === 1 ? 'badge-success' : 'badge-secondary'">
                  {{ row.status === 1 ? $t("modern_trade.company.active") : $t("modern_trade.company.inactive") }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="120" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="info" @click.native="openEdit(row)">
                  <i class="fa fa-edit"></i>
                </base-button>
                <base-button
                  v-if="isEditModernTrade"
                  size="sm"
                  type="danger"
                  @click.native="handleDelete(row)"
                >
                  <i class="fa fa-trash"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">
            {{ $t("total") }}: {{ total }}
          </p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadData"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- Create / Edit Dialog -->
    <el-dialog
      :title="isEdit ? $t('modern_trade.company.edit') : $t('modern_trade.company.create')"
      :visible.sync="showDialog"
      width="600px"
      @closed="resetForm"
    >
      <div class="row">
        <div class="col-md-8">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.name") }} *</label>
            <input v-model="form.name" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-md-8">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.tax_id") }} * (13 หลัก)</label>
            <input
              v-model="form.tax_id"
              class="form-control"
              type="text"
              maxlength="13"
              :disabled="isEdit && formHasMovements"
            />
            <small v-if="isEdit && formHasMovements" class="text-muted">
              {{ $t("modern_trade.company.tax_id_locked_hint") }}
            </small>
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.branch_code") }}</label>
            <input v-model="form.branch_code" class="form-control" type="text" maxlength="5" :disabled="isEdit && formHasMovements" />
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.address") }}</label>
            <textarea v-model="form.address" class="form-control" rows="2"></textarea>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.contact_name") }}</label>
            <input v-model="form.contact_name" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.contact_tel") }}</label>
            <input v-model="form.contact_tel" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.contact_email") }}</label>
            <input v-model="form.contact_email" class="form-control" type="email" />
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.company.status") }}</label>
            <el-select v-model="form.status" class="w-100">
              <el-option :label="$t('modern_trade.company.active')" :value="1"></el-option>
              <el-option :label="$t('modern_trade.company.inactive')" :value="0"></el-option>
            </el-select>
          </div>
        </div>

        <!-- Price profile (MT v2 #148) -->
        <div class="col-12 mt-2 mb-2">
          <h5 class="text-primary mb-0">{{ profileLabels.section }}</h5>
          <hr class="my-2" />
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label d-block">{{ profileLabels.vat_included }}</label>
            <el-switch v-model="form.vat_included" :active-value="1" :inactive-value="0"></el-switch>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label d-block">{{ profileLabels.gp_percent }}</label>
            <el-input-number v-model="form.gp_percent" :min="0" :max="100" :precision="2" :step="0.5" controls-position="right" class="w-100"></el-input-number>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label d-block">{{ profileLabels.credit_term_days }}</label>
            <el-input-number v-model="form.credit_term_days" :min="0" :step="1" controls-position="right" class="w-100"></el-input-number>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label">{{ profileLabels.price_template_id }}</label>
            <el-select v-model="form.price_template_id" class="w-100" clearable :placeholder="profileLabels.no_template">
              <el-option
                v-for="t in priceTemplates"
                :key="t.id"
                :label="t.name"
                :value="t.id"
              ></el-option>
            </el-select>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showDialog = false">{{ $t("close") }}</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Button, Switch, InputNumber } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtCompanies",
  components: {
    RouteBreadCrumb,
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [Switch.name]: Switch,
    [InputNumber.name]: InputNumber,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    // i18n keys added under modern_trade.company.* (#153).
    profileLabels() {
      return {
        section: this.$t("modern_trade.company.price_profile") || "โปรไฟล์ราคา",
        vat_included: this.$t("modern_trade.company.vat_included") || "ราคาปลีกรวม VAT",
        gp_percent: this.$t("modern_trade.company.gp_percent") || "GP (%)",
        credit_term_days: this.$t("modern_trade.company.credit_term_days") || "เครดิต (วัน)",
        price_template_id: this.$t("modern_trade.company.price_template") || "เทมเพลตราคา",
        no_template: this.$t("modern_trade.company.no_template") || "ไม่ผูกเทมเพลต",
      };
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      search: "",
      statusFilter: null,
      showDialog: false,
      isEdit: false,
      formHasMovements: false,
      priceTemplates: [],
      form: {
        name: "",
        tax_id: "",
        branch_code: "00000",
        address: "",
        contact_name: "",
        contact_tel: "",
        contact_email: "",
        status: 1,
        vat_included: 1,
        gp_percent: 0,
        credit_term_days: 0,
        price_template_id: null,
      },
      editId: null,
    };
  },
  mounted() {
    this.loadPriceTemplates();
    this.loadData();
  },
  methods: {
    async loadPriceTemplates() {
      try {
        const res = await ModernTradeService.priceTemplatesList({ per_page: 200, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.priceTemplates = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.search) params.search = this.search;
        if (this.statusFilter !== null && this.statusFilter !== "") params.status = this.statusFilter;
        const res = await ModernTradeService.companiesList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.isEdit = false;
      this.editId = null;
      this.formHasMovements = false;
      this.resetForm();
      this.showDialog = true;
    },
    openEdit(row) {
      this.isEdit = true;
      this.editId = row.id;
      this.formHasMovements = !!(row.has_movements);
      this.form = {
        name: row.name || "",
        tax_id: row.tax_id || "",
        branch_code: row.branch_code || "00000",
        address: row.address || "",
        contact_name: row.contact_name || "",
        contact_tel: row.contact_tel || "",
        contact_email: row.contact_email || "",
        status: row.status !== undefined ? row.status : 1,
        vat_included: row.vat_included !== undefined && row.vat_included !== null ? row.vat_included : 1,
        gp_percent: row.gp_percent !== undefined && row.gp_percent !== null ? Number(row.gp_percent) : 0,
        credit_term_days: row.credit_term_days !== undefined && row.credit_term_days !== null ? Number(row.credit_term_days) : 0,
        price_template_id: row.price_template_id !== undefined && row.price_template_id !== null ? row.price_template_id : null,
      };
      this.showDialog = true;
    },
    resetForm() {
      this.form = {
        name: "",
        tax_id: "",
        branch_code: "00000",
        address: "",
        contact_name: "",
        contact_tel: "",
        contact_email: "",
        status: 1,
        vat_included: 1,
        gp_percent: 0,
        credit_term_days: 0,
        price_template_id: null,
      };
    },
    async handleSave() {
      if (!this.form.name) {
        swal.fire({ title: this.$t("modern_trade.company.name_required"), icon: "warning" });
        return;
      }
      if (!this.form.tax_id || this.form.tax_id.length !== 13) {
        swal.fire({ title: this.$t("modern_trade.company.tax_id_invalid"), icon: "warning" });
        return;
      }
      this.saving = true;
      try {
        if (this.isEdit) {
          await ModernTradeService.companyUpdate(this.editId, this.form);
        } else {
          await ModernTradeService.companyCreate(this.form);
        }
        this.showDialog = false;
        this.loadData();
      } catch (err) {
        const status = err.response && err.response.status;
        const cause = (err.response && err.response.data && err.response.data.Cause) || err.message;
        // BE returns 409 with a has_movements cause when tax_id/branch is changed
        // on a company that already has stock movements — surface it clearly.
        let title = cause || this.$t("modern_trade.company.duplicate_tax_id");
        if (status === 409 && cause && /has_movements|movement/i.test(cause)) {
          title = this.$t("modern_trade.company.has_movements_cannot_edit");
        }
        swal.fire({ title, icon: "error" });
      } finally {
        this.saving = false;
      }
    },
    async handleDelete(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.company.delete_confirm"),
        text: row.name,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      if (!result.value) return;
      try {
        await ModernTradeService.companyDelete(row.id);
        this.loadData();
      } catch (err) {
        const cause = (err.response && err.response.data && err.response.data.Cause) || err.message;
        swal.fire({ title: cause || this.$t("modern_trade.company.has_stock_cannot_delete"), icon: "error" });
      }
    },
  },
};
</script>
